import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'
import { extractApiErrorMessage } from '../../../utils/apiError'
import type { RootState } from '../../store'

// ============================================================================
// TYPES
// ============================================================================

/**
 * Search mode options accepted by the approver-proxy search API.
 * These map 1:1 to the API `searchOption` query param.
 */
export type ApproverProxySearchOption = 'name' | 'nuid' | 'approvalGrpId'

/**
 * Payload shape for the approver proxy search thunk.
 * - `searchOption='name'`          → provide `searchLName` (required, min 2) and optional `searchFName`
 * - `searchOption='nuid'`          → provide `searchValue` (the NUID)
 * - `searchOption='approvalGrpId'` → provide `searchValue` (the Approval Group ID)
 */
export interface ApproverProxySearchParams {
  /** Which field the search is performed against. */
  searchOption: ApproverProxySearchOption
  /** The NUID or Approval Group ID; used when searchOption is 'nuid' or 'approvalGrpId'. */
  searchValue?: string
  /** First name; optional and only meaningful when searchOption is 'name'. */
  searchFName?: string
  /** Last name; required (min length 2) when searchOption is 'name'. */
  searchLName?: string
}

/**
 * Shape of a single record returned by the BFF approver-proxy search API.
 * Fields match the actual API response keys (`GET /tort/approver-proxy/searchAP`).
 */
export interface ApproverProxyRecord {
  /** Approval group identifier, e.g. "00001419". */
  approvalGrpId: string
  /** Geographic region display name, e.g. "Southern California". */
  region: string
  /** Region code for assignment operations, e.g. "NCA", "SCA". */
  regionCode: string
  /** Department name and code. */
  department: string
  /** Primary approver name and NUID, e.g. "CAMPOS, RUBEN  (X166248)". */
  primaryApprover: string
  /** Approver proxies joined by "; ". Empty string when none are assigned. */
  approverProxy: string
  [key: string]: unknown
}

/**
 * Shape of a single record returned by the fetchCurrentAP API
 * (`GET /tort/approver-proxy/fetchCurrentAP`). Represents one currently
 * active approver-proxy assignment for an approval group.
 */
export interface CurrentApproverProxyRecord {
  /** Full proxy name and NUID, e.g. "Nunez, Richard Cruz (B302082)". */
  name: string
  /** Geographic region, e.g. "Southern California". */
  region: string
  /** Department name, or null when unavailable. */
  department: string | null
  /** Proxy start date, or null when open-ended. */
  from: string | null
  /** Proxy end date, or null when open-ended. */
  to: string | null
  /** Proxy email address, or null when unavailable. */
  email: string | null
}

/**
 * Search mode options accepted by the searchAvailableEmployees API.
 * Employee search supports only name- or nuid-based lookups.
 */
export type AvailableEmployeeSearchOption = 'name' | 'nuid'

/**
 * Payload shape for the {@link searchAvailableEmployees} thunk.
 * - `searchOption='name'` → provide `searchLName` (required, min 2) and optional `searchFName`
 * - `searchOption='nuid'` → provide `searchValue` (the employee NUID)
 */
export interface AvailableEmployeeSearchParams {
  /** Approval group the candidate would be added to (digits-only per contract). */
  approvalGrpId: string
  /** Which field the search is performed against. */
  searchOption: AvailableEmployeeSearchOption
  /** First name; optional and only meaningful when searchOption is 'name'. */
  searchFName?: string
  /** Last name; required (min length 2) when searchOption is 'name'. */
  searchLName?: string
  /** Employee NUID; required when searchOption is 'nuid'. */
  searchValue?: string
}

/**
 * Shape of a single record returned by the searchAvailableEmployees API
 * (`GET /tort/approver-proxy/searchAvailableEmployees`). Represents one
 * employee eligible to be assigned as an approver proxy for the group.
 */
export interface AvailableEmployeeRecord {
  /** Full employee name and NUID, e.g. "Smith, John A (K061228)". */
  name: string
  /** Geographic region, e.g. "Southern California". */
  region: string
  /** Department name and code. */
  department: string
  /** Employee email address. */
  email: string
}

/** State shape for the approverProxy slice. */
export interface ApproverProxyState {
  /** True while the search API call is in-flight. */
  isLoading: boolean
  /** True once the user has submitted at least one search — controls empty-state visibility. */
  searchPerformed: boolean
  /** Search results returned by the API. Empty array when no matches. */
  data: ApproverProxyRecord[]
  /** Human-readable error message when the API call fails; null otherwise. */
  error: string | null
  /** True while the fetchCurrentAP API call is in-flight. */
  isLoadingFetchCurrent: boolean
  /** Current approver-proxy records fetched on Edit click. */
  currentProxies: CurrentApproverProxyRecord[]
  /** Error from the fetchCurrentAP call, or null. */
  fetchCurrentError: string | null
  /** True while the searchAvailableEmployees API call is in-flight. */
  isLoadingEmployeeSearch: boolean
  /** True once an employee search has been submitted — controls empty-state visibility. */
  employeeSearchPerformed: boolean
  /** Eligible employees returned by the most recent employee search. */
  employeeResults: AvailableEmployeeRecord[]
  /** Error from the searchAvailableEmployees call, or null. */
  employeeSearchError: string | null
  /** True while the assignApproverProxies API call is in-flight. */
  isLoadingSave: boolean
  /** Error from the most recent failed assignApproverProxies call, or null. */
  saveError: string | null
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: ApproverProxyState = {
  isLoading: false,
  searchPerformed: false,
  data: [],
  error: null,
  isLoadingFetchCurrent: false,
  currentProxies: [],
  fetchCurrentError: null,
  isLoadingEmployeeSearch: false,
  employeeSearchPerformed: false,
  employeeResults: [],
  employeeSearchError: null,
  isLoadingSave: false,
  saveError: null,
}

// ============================================================================
// ASYNC THUNK
// ============================================================================

/**
 * Searches for approver proxy records via the BFF search endpoint.
 *
 * GET /tort/approver-proxy/searchAP
 *
 * Query params built from the payload:
 * - Always:          actionCode=searchAP, searchOption=<mode>
 * - name:            searchLName=<last> (required, min 2), searchFName=<first> (optional)
 * - nuid|group:      searchValue=<input>
 *
 * The `x-nuid` identity header is attached by the shared Axios interceptor.
 *
 * @param payload - Search option and corresponding field values.
 * @returns Array of matching {@link ApproverProxyRecord} objects (empty when none).
 */
export const searchApproverProxy = createAsyncThunk<
  ApproverProxyRecord[],
  ApproverProxySearchParams
>(
  'admin/approverProxy/search',
  async (payload, { rejectWithValue, signal }) => {
    try {
      const params: Record<string, string> = {
        actionCode: 'searchAP',
        searchOption: payload.searchOption,
      }

      if (payload.searchOption === 'name') {
        // Last name is required by the contract; first name is optional.
        if (payload.searchLName) params.searchLName = payload.searchLName
        if (payload.searchFName) params.searchFName = payload.searchFName
      } else if (payload.searchValue) {
        // Single value for nuid or approvalGrpId searches.
        params.searchValue = payload.searchValue
      }

      const response = await api.get<ApproverProxyRecord[]>(
        endpoints.admin.searchApproverProxy,
        { params, signal }
      )
      return response.data
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to search approver proxy records')
      )
    }
  }
)

/**
 * Fetches the currently active approver-proxy assignments for an approval group.
 *
 * GET /tort/approver-proxy/fetchCurrentAP
 *
 * Query params:
 * - `actionCode=fetchCurrentAP` (constant)
 * - `timeStamp=<epoch ms>` — client sysdate as a 13-digit Unix millisecond
 *   timestamp (e.g. `1782990446120`), generated here for server-side auditing
 * - `approvalGrpId=<id>` — the target approval group
 *
 * Only assignments active as of the server sysdate are returned; an empty array
 * indicates none are active. The `x-nuid` identity header is attached by the
 * shared Axios interceptor.
 *
 * @param approvalGrpId - The approval group ID to fetch current proxies for.
 * @returns Array of {@link CurrentApproverProxyRecord} objects (empty when none).
 */
export const fetchCurrentApproverProxies = createAsyncThunk<
  CurrentApproverProxyRecord[],
  string
>(
  'admin/approverProxy/fetchCurrent',
  async (approvalGrpId, { rejectWithValue, signal }) => {
    try {
      const response = await api.get<CurrentApproverProxyRecord[]>(
        endpoints.admin.fetchCurrentApproverProxies,
        {
          params: {
            actionCode: 'fetchCurrentAP',
            // 13-digit Unix millisecond epoch (e.g. 1782990446120), sent as a
            // string to satisfy the `timeStamp: string` server contract.
            timeStamp: Date.now().toString(),
            approvalGrpId,
          },
          signal,
        }
      )
      return response.data
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to fetch current approver proxy records')
      )
    }
  }
)

/**
 * A single approver-proxy assignment entry included in the save payload.
 */
export interface AssignApproverProxyEntry {
  /** Proxy assignment start date in MM/DD/YYYY format. */
  fromDate: string
  /** Proxy assignment end date in MM/DD/YYYY format. */
  toDate: string
  /** Geographic region for the assignment, e.g. "Southern California". */
  region: string
  /** Department name for the assignment. */
  department: string
  /** Full display name of the selected proxy, e.g. "Arechiga, Yolanda (O275197)". */
  selectedAPProxyName: string
  /** Proxy email address. */
  email: string
  /**
   * Flag indicating whether this proxy was newly added in this save operation.
   * `"1"` = newly added proxy; `"0"` = existing proxy carried over from fetchCurrentAP.
   */
  existingApproverProxy: string
}

/**
 * Payload shape for the {@link assignApproverProxies} thunk.
 */
export interface AssignApproverProxiesPayload {
  /** Approval group identifier to update. */
  approvalGrpId: string
  /** Region code for the approval group (from the search result). */
  regionCode: string
  /** Full name of the primary approver for the group. */
  primaryApproverName: string
  /** NUID of the currently logged-in user making the change. */
  proxyApproverNuid: string
  /** True when all proxies are being removed (saving an empty list). */
  saveNone: boolean
  /** The updated list of approver-proxy assignments after the user's edits. */
  currentApproverProxies: AssignApproverProxyEntry[]
}

/**
 * Saves the current approver-proxy assignments for an approval group.
 *
 * POST /tort/approver-proxy/assignApproverProxies
 *
 * @param payload - The approval group details and updated proxy list.
 * @returns void on success.
 */
export const assignApproverProxies = createAsyncThunk<
  void,
  AssignApproverProxiesPayload
>(
  'admin/approverProxy/assign',
  async (payload, { rejectWithValue }) => {
    try {
      await api.post(endpoints.admin.assignApproverProxies, {
        actionCode: 'assignAP',
        approvalGrpId: payload.approvalGrpId,
        regionCode: payload.regionCode,
        primaryApproverName: payload.primaryApproverName,
        proxyApproverNuid: payload.proxyApproverNuid,
        saveNone: payload.saveNone,
        currentApproverProxies: payload.currentApproverProxies,
      })
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to save approver proxy assignments')
      )
    }
  }
)

/**
 * Searches for employees eligible to be added as an approver proxy for a group.
 *
 * GET /tort/approver-proxy/searchAvailableEmployees
 *
 * The API excludes employees who are already the primary approver or an existing
 * proxy for the group. Query params built from the payload:
 * - Always: `actionCode=empAPSearch`, `approvalGrpId=<digits>`, `searchOption=<mode>`
 * - name:   `searchLName=<last>` (required, min 2), `searchFName=<first>` (optional)
 * - nuid:   `searchValue=<nuid>`
 *
 * The `x-nuid` identity header is attached by the shared Axios interceptor.
 *
 * @param payload - Approval group, search option, and corresponding field values.
 * @returns Array of matching {@link AvailableEmployeeRecord} objects (empty when none).
 */
export const searchAvailableEmployees = createAsyncThunk<
  AvailableEmployeeRecord[],
  AvailableEmployeeSearchParams
>(
  'admin/approverProxy/searchAvailableEmployees',
  async (payload, { rejectWithValue, signal }) => {
    try {
      const params: Record<string, string> = {
        actionCode: 'empAPSearch',
        approvalGrpId: payload.approvalGrpId,
        searchOption: payload.searchOption,
      }

      if (payload.searchOption === 'name') {
        // Last name is required (min 2) by the contract; first name is optional.
        if (payload.searchLName) params.searchLName = payload.searchLName
        if (payload.searchFName) params.searchFName = payload.searchFName
      } else if (payload.searchValue) {
        // NUID lookup uses the single searchValue param.
        params.searchValue = payload.searchValue
      }

      const response = await api.get<AvailableEmployeeRecord[]>(
        endpoints.admin.searchAvailableEmployees,
        { params, signal }
      )
      return response.data
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to search available employees')
      )
    }
  }
)

// ============================================================================
// SLICE
// ============================================================================

const approverProxySlice = createSlice({
  name: 'approverProxy',
  initialState,
  reducers: {
    /**
     * Resets the slice to its initial state.
     * Call this when navigating away from the approver-proxy search page
     * to avoid stale results appearing on re-entry.
     */
    clearApproverProxyState: (state) => {
      state.isLoading = false
      state.searchPerformed = false
      state.data = []
      state.error = null
      state.isLoadingFetchCurrent = false
      state.currentProxies = []
      state.fetchCurrentError = null
      state.isLoadingEmployeeSearch = false
      state.employeeSearchPerformed = false
      state.employeeResults = []
      state.employeeSearchError = null
      state.isLoadingSave = false
      state.saveError = null
    },
  },
  extraReducers: (builder) => {
    // ── searchApproverProxy ─────────────────────────────────────────────────
    builder.addCase(searchApproverProxy.pending, (state) => {
      state.isLoading = true
      state.searchPerformed = true
      state.error = null
      state.data = []
      // Clear stale edit-panel errors and employee search results so they do
      // not persist across searches.
      state.saveError = null
      state.employeeSearchError = null
      state.employeeResults = []
      state.employeeSearchPerformed = false
    })
    builder.addCase(searchApproverProxy.fulfilled, (state, action) => {
      state.isLoading = false
      state.data = action.payload
    })
    builder.addCase(searchApproverProxy.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload as string
    })
    // ── fetchCurrentApproverProxies ─────────────────────────────────────────
    builder.addCase(fetchCurrentApproverProxies.pending, (state) => {
      state.isLoadingFetchCurrent = true
      state.fetchCurrentError = null
      state.currentProxies = []
    })
    builder.addCase(fetchCurrentApproverProxies.fulfilled, (state, action) => {
      state.isLoadingFetchCurrent = false
      state.currentProxies = action.payload
    })
    builder.addCase(fetchCurrentApproverProxies.rejected, (state, action) => {
      state.isLoadingFetchCurrent = false
      state.fetchCurrentError = action.payload as string
    })
    // ── searchAvailableEmployees ────────────────────────────────────────────
    builder.addCase(searchAvailableEmployees.pending, (state) => {
      state.isLoadingEmployeeSearch = true
      state.employeeSearchPerformed = true
      state.employeeSearchError = null
      state.employeeResults = []
    })
    builder.addCase(searchAvailableEmployees.fulfilled, (state, action) => {
      state.isLoadingEmployeeSearch = false
      state.employeeResults = action.payload
    })
    builder.addCase(searchAvailableEmployees.rejected, (state, action) => {
      state.isLoadingEmployeeSearch = false
      state.employeeSearchError = action.payload as string
    })
    // ── assignApproverProxies ───────────────────────────────────────────────
    builder.addCase(assignApproverProxies.pending, (state) => {
      state.isLoadingSave = true
      state.saveError = null
    })
    builder.addCase(assignApproverProxies.fulfilled, (state) => {
      state.isLoadingSave = false
    })
    builder.addCase(assignApproverProxies.rejected, (state, action) => {
      state.isLoadingSave = false
      state.saveError = action.payload as string
    })
  },
})

export const { clearApproverProxyState } = approverProxySlice.actions

// ============================================================================
// SELECTORS
// ============================================================================

/** True while the approver proxy search API call is in-flight. */
export const selectApproverProxyLoading = (state: RootState): boolean =>
  state.approverProxy.isLoading

/** True once the user has performed at least one approver proxy search. */
export const selectApproverProxySearchPerformed = (state: RootState): boolean =>
  state.approverProxy.searchPerformed

/** Search results from the most recent approver proxy search. */
export const selectApproverProxyData = (state: RootState): ApproverProxyRecord[] =>
  state.approverProxy.data

/** Error message from the most recent failed approver proxy search, or null. */
export const selectApproverProxyError = (state: RootState): string | null =>
  state.approverProxy.error

/** True while the fetchCurrentAP API call is in-flight. */
export const selectApproverFetchCurrentLoading = (state: RootState): boolean =>
  state.approverProxy.isLoadingFetchCurrent

/** Current approver-proxy records fetched by the most recent fetchCurrentAP call. */
export const selectApproverCurrentProxies = (
  state: RootState
): CurrentApproverProxyRecord[] => state.approverProxy.currentProxies

/** Error message from the most recent failed fetchCurrentAP call, or null. */
export const selectApproverFetchCurrentError = (state: RootState): string | null =>
  state.approverProxy.fetchCurrentError

/** True while the searchAvailableEmployees API call is in-flight. */
export const selectAvailableEmployeeLoading = (state: RootState): boolean =>
  state.approverProxy.isLoadingEmployeeSearch

/** True once the user has performed at least one available-employee search. */
export const selectAvailableEmployeeSearchPerformed = (state: RootState): boolean =>
  state.approverProxy.employeeSearchPerformed

/** Eligible employees returned by the most recent available-employee search. */
export const selectAvailableEmployeeResults = (
  state: RootState
): AvailableEmployeeRecord[] => state.approverProxy.employeeResults

/** Error message from the most recent failed available-employee search, or null. */
export const selectAvailableEmployeeError = (state: RootState): string | null =>
  state.approverProxy.employeeSearchError

/** True while the assignApproverProxies API call is in-flight. */
export const selectAPSaveLoading = (state: RootState): boolean =>
  state.approverProxy.isLoadingSave

/** Error from the most recent failed assignApproverProxies call, or null. */
export const selectAPSaveError = (state: RootState): string | null =>
  state.approverProxy.saveError

export default approverProxySlice.reducer
